<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import SiteAdminService from '@/services/SiteAdminService'
import { urlError, isRelativePath } from '@/Utils/siteUrl'
import IconBase from '@/components/icon/IconBase.vue'
import IconCloudUpload from '@/components/icon/IconCloudUpload.vue'

// Image field for the site: upload to the site's R2 bucket through castr's
// proxy (site-admin API `POST /uploads`, multipart field `file`, ≤ 10 MB,
// png/jpeg/gif/webp/avif — the runtime sniffs the bytes and refuses SVG).
// Upload only (Trello qG88dqXo): there is no URL input. A value saved earlier
// as an external URL still previews and can be replaced or removed; the
// runtime API keeps accepting URLs for older clients.
//
// Preview (round 6, #8): a saved `/upload/<file>` path is previewed on the
// site's own origin; a failed load is retried on demand and is not sticky
// across value changes. Until the site is published its runtime answers the
// offline page for `/upload/*` too (middleware `siteUnavailableResponse`), so
// the preview says so instead of claiming the image is broken.
const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	siteId: {
		type: String,
		required: true,
	},
	label: {
		type: String,
		default: 'Image',
	},
	id: {
		type: String,
		default: undefined,
	},
	// Recommended size / format, shown under the label.
	hint: {
		type: String,
		default: '',
	},
	// Soft size guidance (bytes): larger files still upload, with a warning.
	recommendedMaxBytes: {
		type: Number,
		default: 0,
	},
	// How the preview is framed: 'wide' (16:9 card), 'logo' (wide, contained), 'square' (favicon).
	preview: {
		type: String,
		default: 'wide',
	},
	// Image used when the field is empty (e.g. the thumbnail castr resolves
	// from the linked stream). Shown as a preview so the owner sees what the
	// site will display and can upload their own to override it.
	fallback: {
		type: String,
		default: '',
	},
	fallbackLabel: {
		type: String,
		default: 'Default image',
	},
})
const emit = defineEmits(['update:modelValue'])
const { notify } = useNotification()
const store = useStore()
const site = computed(() => store.getters['Sites/siteById']?.(props.siteId) || null)

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/avif'
const ACCEPTED_TYPES = ACCEPT.split(',')
const MAX_BYTES = 10 * 1024 * 1024

const fileInput = ref(null)
const uploading = ref(false)
const dragging = ref(false)
const broken = ref(false)
const reloadKey = ref(0)

const value = computed({
	get: () => props.modelValue || '',
	set: (v) => { broken.value = false; emit('update:modelValue', v) },
})
// A value set by the parent (reopened form, saved answer) starts with a fresh preview.
watch(() => props.modelValue, () => { broken.value = false })
const isDataUrl = computed(() => /^data:image\//i.test(value.value))
const valueError = computed(() => (value.value && !isDataUrl.value ? urlError(value.value, { allowRelative: true }) : null))

const siteOrigin = computed(() => {
	try { return site.value?.url ? new URL(site.value.url).origin : '' } catch { return '' }
})
/** What the <img> loads: relative `/upload/…` → on the site's origin; a retry appends a cache-buster. */
const previewSrc = computed(() => {
	const v = value.value
	if (!v || isDataUrl.value) return v
	const abs = isRelativePath(v) ? `${siteOrigin.value}${v}` : v
	return reloadKey.value ? `${abs}${abs.includes('?') ? '&' : '?'}r=${reloadKey.value}` : abs
})
const onSiteHost = computed(() => {
	if (!previewSrc.value || isDataUrl.value || !site.value) return false
	try {
		const host = new URL(previewSrc.value).host
		const hosts = [site.value.primaryHost, site.value.canonicalHost, ...(site.value.customDomains || []).map((d) => d.hostname || d.host)].filter(Boolean)
		return hosts.includes(host)
	} catch { return false }
})
// Unpublished / disabled site: the runtime serves its offline page for /upload/* as well.
const previewOffline = computed(() => broken.value && onSiteHost.value && Boolean(site.value) && site.value.status !== 'active')
const retryPreview = () => { broken.value = false; reloadKey.value = Date.now() }
const isOverride = computed(() => Boolean(value.value && props.fallback && value.value !== props.fallback))

const previewFrame = computed(() => {
	if (props.preview === 'square') return 'size-16 rounded-lg p-1.5'
	if (props.preview === 'logo') return 'h-16 w-40 rounded-lg p-2'
	return 'h-14 w-24 rounded-md'
})
const previewFit = computed(() => (props.preview === 'wide' ? 'object-cover' : 'object-contain'))

const pick = () => fileInput.value?.click()

const formatBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(n % (1024 * 1024) ? 1 : 0)} MB` : `${Math.round(n / 1024)} KB`)

async function upload(file) {
	if (!file) return
	if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
		notify({ group: 'error', text: file.type === 'image/svg+xml' ? 'SVG files are not accepted — export the image as PNG or WebP.' : 'Use a PNG, JPEG, GIF, WebP or AVIF image.' })
		return
	}
	if (file.size > MAX_BYTES) {
		notify({ group: 'error', text: 'Images must be 10 MB or smaller.' })
		return
	}
	uploading.value = true
	try {
		const res = await SiteAdminService.uploadImage(props.siteId, file)
		value.value = res.absoluteUrl || res.url
		const big = props.recommendedMaxBytes && file.size > props.recommendedMaxBytes
		notify({ group: 'success', text: big ? `Image uploaded (${formatBytes(file.size)}). Keep it under ${formatBytes(props.recommendedMaxBytes)} for faster page loads.` : 'Image uploaded.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Upload failed.' })
	}
	uploading.value = false
}

const onFile = (event) => {
	const file = event.target.files?.[0]
	event.target.value = ''
	upload(file)
}
const onDrop = (event) => {
	dragging.value = false
	upload(event.dataTransfer?.files?.[0])
}
</script>

<template>
	<div class="min-w-0" :data-testid="id ? `${id}-field` : undefined">
		<div class="mb-2 flex flex-wrap items-baseline gap-x-2">
			<label class="mb-0 text-tiny font-medium text-surface-text-1" :for="id">{{ label }}</label>
			<span v-if="hint" class="text-tiny text-surface-7">{{ hint }}</span>
		</div>

		<div class="flex min-w-0 flex-wrap items-start gap-3">
			<!-- Preview: current value, or the fallback the site would use -->
			<div v-if="value && !valueError" class="shrink-0 overflow-hidden bg-surface-3 ring-1 ring-surface-4" :class="previewFrame" :title="value" data-testid="image-preview" :data-state="previewOffline ? 'offline' : broken ? 'error' : 'ok'">
				<img v-if="!broken" :key="previewSrc" :src="previewSrc" alt="" class="size-full" :class="previewFit" @error="broken = true">
				<div v-else-if="previewOffline" class="grid size-full place-items-center px-1 text-center text-[10px] leading-tight text-surface-7" title="The image is saved. Its address only answers once the site is published.">Saved · preview after publish</div>
				<button v-else type="button" class="grid size-full place-items-center px-1 text-center text-[10px] leading-tight text-error hover:bg-surface-4" title="Try loading the image again" data-testid="image-preview-retry" @click="retryPreview">Image failed to load · retry</button>
			</div>
			<div v-else-if="fallback" class="shrink-0 overflow-hidden bg-surface-3 ring-1 ring-surface-4" :class="previewFrame" data-testid="image-fallback" :title="fallback">
				<img :src="fallback" alt="" class="size-full" :class="previewFit">
			</div>

			<!-- Drop zone / upload button -->
			<button :id="id" type="button"
				class="flex min-w-0 flex-1 basis-40 items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 text-left transition-colors disabled:opacity-60"
				:class="dragging ? 'border-main bg-main-transparent' : 'border-surface-5 bg-surface-3 hover:border-surface-6 hover:bg-surface-4'"
				:disabled="uploading" :aria-label="`Upload ${label.toLowerCase()}`"
				@click="pick" @dragover.prevent="dragging = true" @dragleave="dragging = false" @drop.prevent="onDrop">
				<icon-base class="shrink-0 text-surface-7" :width="18" :height="18"><icon-cloud-upload /></icon-base>
				<span class="min-w-0">
					<span class="block text-sm font-medium text-surface-text-1">{{ uploading ? 'Uploading…' : value ? 'Replace image' : 'Upload image' }}</span>
					<span class="block truncate text-tiny text-surface-7">PNG, JPEG, GIF, WebP or AVIF · up to 10 MB · or drop a file here</span>
				</span>
			</button>
			<input ref="fileInput" type="file" :accept="ACCEPT" class="hidden" @change="onFile">
		</div>

		<div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-tiny">
			<span v-if="value && fallback && !isOverride" class="text-surface-7">{{ fallbackLabel }}</span>
			<span v-else-if="!value && fallback" class="text-surface-7" data-testid="image-fallback-note">{{ fallbackLabel }} — upload your own to override.</span>
			<button v-if="isOverride" type="button" class="text-surface-8 underline hover:text-white" @click="value = ''">
				Use {{ fallbackLabel.toLowerCase() }}
			</button>
			<button v-else-if="value" type="button" class="text-surface-8 underline hover:text-white" @click="value = ''">Remove</button>
			<span v-if="value && !valueError" class="min-w-0 max-w-full truncate text-surface-6" :title="value">{{ value }}</span>
		</div>

		<p v-if="valueError" class="mb-0 mt-2 text-tiny text-error" data-testid="image-invalid">The saved image address can't be used. Upload a new image or remove it.</p>
	</div>
</template>
